/* JsonDiffControlComponent.js */

"use strict";

const EventEmitter = require("events").EventEmitter;
const JsonDiffControlRenderer = require("./JsonDiffControlRenderer");
const _ = require("lodash");
//require("ace-builds/src-noconflict/mode-json");
class JsonDiffControlComponent extends EventEmitter {
    /**
     * @schema.name Json Diff Control
     * @schema.package @gluenetworks/json_diff_control
     * @schema.packageIdentifier JsonDiffControlComponent
     * @schema.namespace require("@gluenetworks/json_diff_control").JsonDiffControlComponent
     *
     * @schema.configuration.firstJson.name firstJson
     * @schema.configuration.firstJson.type Object
     * @schema.configuration.firstJson.default {}
     * @schema.configuration.firstJson.required true
     *
     * @schema.configuration.secondJson.name secondJson
     * @schema.configuration.secondJson.type Object
     * @schema.configuration.secondJson.default {}
     * @schema.configuration.secondJson.required true
     *
     * @schema.configuration.scrollSynchronized.name scrollSynchronized
     * @schema.configuration.scrollSynchronized.type Boolean
     * @schema.configuration.scrollSynchronized.default true
     * @schema.configuration.scrollSynchronized.required true
     *
     * @schema.configuration.compactView.name compactView
     * @schema.configuration.compactView.type Boolean
     * @schema.configuration.compactView.default false
     * @schema.configuration.compactView.required false
     *
     * @schema.configuration.sideBySideView.name sideBySideView
     * @schema.configuration.sideBySideView.type Boolean
     * @schema.configuration.sideBySideView.default true
     * @schema.configuration.sideBySideView.required false
     **/
    constructor(config) {
        super(config);
        this._scrollSynchronized = _.get(config, "scrollSynchronized", true);
        this._compactView = _.get(config, "compactView", false);
        this._sideBySideView = _.get(config, "sideBySideView", true);
        this._firstJson = _.get(config, "firstJson", {}); //require('../sample_data/tfstate1'));
        this._secondJson = _.get(config, "secondJson", {}); //require('../sample_data/tfstate2'));
        this._theme = _.get(config, "theme", "dark");
        this._hasDownload = _.get(config, "download", false); // ToDo: use this to show or hide download button
        this._context = _.get(config, "context", this);
        this._downloadCB = _.get(config, "downloadCB");
        this._userActions = _.get(config, "actions", {});
        this._actionsList = Object.values(this._userActions);
        this._hasDiff = undefined;
        this._markers = undefined;
        this._renderer = new JsonDiffControlRenderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        if (!this.renderer) {
            return Promise.resolve();
        } else {
            return this._renderer.shutdown().then(
                () => {
                    this._firstJson = undefined;
                    this._secondJson = undefined;
                    this._scrollSynchronized = undefined;
                    this._compactView = undefined;
                    this._sideBySideView = undefined;
                    this._renderer = undefined;
                    this._markers = undefined;
                    this._hasDiff = undefined;
                },
                () => {
                    //Error handling on renderer shut down
                }
            );
        }
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * @schema.methods.setFirstJson.name setFirstJson
     * @schema.methods.setFirstJson.description Sets first json on the left editor
     * @schema.methods.setFirstJson.returns Instance of the control
     * @schema.methods.setFirstJson.parameters.value.name value
     * @schema.methods.setFirstJson.parameters.value.description first json to compare
     * @schema.methods.setFirstJson.parameters.value.type Object
     */
    setFirstJson(value) {
        if (!_.isEqual(this._firstJson, value)) {
            this._firstJson = value;
            this.emit("FirstJson", this._firstJson);
        }
        return this;
    }

    /**
     * @schema.methods.getFirstJson.name getFirstJson
     * @schema.methods.getFirstJson.description Returns first json
     * @schema.methods.getFirstJson.returns Object
     */
    getFirstJson() {
        return this._firstJson;
    }

    get firstJson() {
        return this.getFirstJson();
    }
    set firstJson(value) {
        this.setFirstJson();
    }

    /**
     * @schema.methods.setSecondJson.name setSecondJson
     * @schema.methods.setSecondJson.description Sets second json on the right editor
     * @schema.methods.setSecondJson.returns Instance of the control
     * @schema.methods.setSecondJson.parameters.value.name value
     * @schema.methods.setSecondJson.parameters.value.description second json to compare
     * @schema.methods.setSecondJson.parameters.value.type Object
     */
    setSecondJson(value) {
        if (!_.isEqual(this._secondJson, value)) {
            this._secondJson = value;
            this.emit("SecondJson", this._secondJson);
        }
        return this;
    }

    /**
     * @schema.methods.getSecondJson.name getSecondJson
     * @schema.methods.getSecondJson.description Returns second json
     * @schema.methods.getSecondJson.returns Object
     */
    getSecondJson() {
        return this._secondJson;
    }

    set secondJson(value) {
        this.setSecondJson(value);
    }

    get secondJson() {
        return this.getSecondJson();
    }

    /**
     * @schema.methods.setScrollSynchronized.name setScrollSynchronized
     * @schema.methods.setScrollSynchronized.description Scroll on editor auto scrolls other editor to show differences in parallel
     *                                                          or as close to each other as possible
     * @schema.methods.setScrollSynchronized.returns Instance of the control
     * @schema.methods.setScrollSynchronized.parameters.scrollSynchronized.name scrollSynchronized
     * @schema.methods.setScrollSynchronized.parameters.scrollSynchronized.description true enables synchronized scroll
     * @schema.methods.setScrollSynchronized.parameters.scrollSynchronized.type Boolean
     */
    setScrollSynchronized(synchronizedScroll) {
        if (synchronizedScroll !== this._scrollSynchronized && _.isBoolean(synchronizedScroll)) {
            this._scrollSynchronized = synchronizedScroll;
            this.emit("scrollSynchronized", synchronizedScroll);
        }
        return this;
    }

    /**
     * @schema.methods.isScrollSynchronized.name isScrollSynchronized
     * @schema.methods.isScrollSynchronized.description true if synchronized scroll enabled else false
     * @schema.methods.isScrollSynchronized.returns Boolean
     */
    isScrollSynchronized() {
        return this._scrollSynchronized;
    }

    set scrollSynchronized(scrollSynchronized) {
        this.setScrollSynchronized(scrollSynchronized);
    }

    get scrollSynchronized() {
        return this.isScrollSynchronized();
    }

    /**
     * @schema.methods.setCompactView.name setCompactView
     * @schema.methods.setCompactView.description  Collapses unchanged rows if true
     * @schema.methods.setCompactView.returns Instance of the control
     * @schema.methods.setCompactView.parameters.compactView.name compactView
     * @schema.methods.setCompactView.parameters.compactView.description true enables compact view
     * @schema.methods.setCompactView.parameters.compactView.type Boolean
     */
    setCompactView(compactView) {
        if (compactView !== this._compactView && _.isBoolean(compactView)) {
            this._compactView = compactView;
            this.emit("compactView", compactView);
        }
        return this;
    }

    /**
     * @schema.methods.isCompactView.name isCompactView
     * @schema.methods.isCompactView.description true if compact view enabled else false
     * @schema.methods.isCompactView.returns Boolean
     */
    isCompactView() {
        return this._compactView;
    }

    set compactView(compactView) {
        this.setCompactView(compactView);
    }

    get compactView() {
        return this.isCompactView();
    }

    /**
     * @schema.methods.setSideBySideView.name setSideBySideView
     * @schema.methods.setSideBySideView.description  If true Shows differences side by side on UI
     *                                                else shows differences in single editor one after the other
     * @schema.methods.setSideBySideView.returns Instance of the control
     * @schema.methods.setSideBySideView.parameters.sideBySideView.name sideBySideView
     * @schema.methods.setSideBySideView.parameters.sideBySideView.description true enables side by side view
     * @schema.methods.setSideBySideView.parameters.sideBySideView.type Boolean
     */
    setSideBySideView(sideBySideView) {
        if (sideBySideView !== this._sideBySideView && _.isBoolean(sideBySideView)) {
            this._sideBySideView = sideBySideView;
            this.emit("sideBySideView", sideBySideView);
        }
        return this;
    }

    /**
     * @schema.methods.isSideBySideView.name isSideBySideView
     * @schema.methods.isSideBySideView.description true if side by side view enabled else false
     * @schema.methods.isSideBySideView.returns Boolean
     */
    isSideBySideView() {
        return this._sideBySideView;
    }

    set sideBySideView(sideBySideView) {
        this.setSideBySideView(sideBySideView);
    }

    get sideBySideView() {
        return this.isSideBySideView();
    }

    setHeight(left, right) {
        this.emit("UpdateHeight", left, right);
    }

    setMarkers(markers = [], silent = false) {
        this._markers = markers;
        if (markers.length === 0) {
            this.setHasDiff(false, silent);
        } else {
            this.setHasDiff(true, silent);
        }
    }

    get hasDiff() {
        return this.getHasDiff();
    }

    getHasDiff() {
        return !!this._hasDiff;
    }

    setHasDiff(hasDiff = false, silent = false) {
        const newHasDiff = !!hasDiff;
        if (!silent) {
            if (newHasDiff) {
                this.emit("JsonDiffHasDiff");
            } else {
                this.emit("JsonDiffNoDiff");
            }
        }
        this._hasDiff = newHasDiff;
    }
}

module.exports = JsonDiffControlComponent;
